import maxBy from 'lodash/maxBy';
import round from 'lodash/round';
import sumBy from 'lodash/sumBy';
import type { SubjectCategory } from '~/Features/StudyPage/Common/Interfaces/SubjectCategory';
import type { RestrictedSingleSubject } from '~/Features/StudyPage/Online/Interfaces/RestrictedSingleSubject';

type NarrowedElectiveCategories = Exclude<SubjectCategory, 'obligatory'>;
const FRACTION_DIGITS = 2;

const isElective = (subjectCategory: SubjectCategory): subjectCategory is NarrowedElectiveCategories =>
    subjectCategory !== 'obligatory';

// This is based on an assumption that elective groups with different number of points are not mixed within one semester.
// Ideally, we should fetch that information from the FS - but current version of the API does not provide it...
export const creditsCalculator = (
    obligatory: { points: Pick<RestrictedSingleSubject['points'], 'amount'> }[],
    electiveSubjectGroups: Map<string, { subjects: Pick<RestrictedSingleSubject, 'subjectCategory'>[] }>,
    semesterCredits: number
) => {
    const obligatoryCreditsPerSemester = sumBy(obligatory, subject => subject.points.amount);

    const electiveSubjectGroupsArray = Array.from(electiveSubjectGroups);
    const calculatedElectives = semesterCredits - obligatoryCreditsPerSemester;

    const creditLimitPerElectiveGroup = round(
        electiveSubjectGroupsArray.length === 0 || calculatedElectives <= 0
            ? 0
            : calculatedElectives / electiveSubjectGroupsArray.length,
        FRACTION_DIGITS
    );

    const getCategoryTagsToElectiveGroupsMapping = () => {
        const determineCategoryGroup = (subjects: Map<NarrowedElectiveCategories, number>) => {
            const maxValue = maxBy(Array.from(subjects.entries()), ([, value]) => value);

            return maxValue ? maxValue[0] : undefined;
        };

        return electiveSubjectGroupsArray.reduce(
            (result, [, electiveGroup]) => {
                const electiveGroupsPerSubjectCategory = electiveGroup.subjects.reduce(
                    (aggregator, subject) => {
                        const subjectCategory = subject.subjectCategory;

                        if (isElective(subjectCategory)) {
                            const currentValue = aggregator.get(subjectCategory);
                            aggregator.set(subjectCategory, (currentValue ?? 0) + 1);
                        }

                        return aggregator;
                    },
                    new Map() as Map<NarrowedElectiveCategories, number>
                );

                const categoryGroup = determineCategoryGroup(electiveGroupsPerSubjectCategory);

                if (categoryGroup === undefined) {
                    return result;
                }

                const currentValue = result.get(categoryGroup);
                result.set(categoryGroup, (currentValue ?? 0) + 1);

                return result;
            },
            new Map() as Map<NarrowedElectiveCategories, number>
        );
    };

    const getTotalPointsPerElectiveGroupCategory = () => {
        if (electiveSubjectGroupsArray.length === 0 || calculatedElectives <= 0) {
            return undefined;
        }

        const categoryTagsToElectiveGroupsMapping = getCategoryTagsToElectiveGroupsMapping();
        const numberOfElectiveGroups = categoryTagsToElectiveGroupsMapping.get('elective') ?? 0;
        const numberOfSpecializationGroups = categoryTagsToElectiveGroupsMapping.get('specialization') ?? 0;

        if (numberOfElectiveGroups <= 0 && numberOfSpecializationGroups <= 0) {
            return undefined;
        }

        const result = new Map<NarrowedElectiveCategories, number>();

        result.set('elective', creditLimitPerElectiveGroup * numberOfElectiveGroups);
        result.set('specialization', creditLimitPerElectiveGroup * numberOfSpecializationGroups);

        return result;
    };

    return {
        obligatoryCreditsPerSemester,
        getTotalPointsPerElectiveGroupCategory,
        creditLimitPerElectiveGroup,
    };
};
